import { Box, Button, ButtonGroup, Chip, CircularProgress, Collapse, Grid, IconButton, LinearProgress, Menu, MenuItem, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useContext, useEffect, useState } from "react";
import { formatDateTime } from "../misc/helperfunctions";
import { UserContext } from "../theme/userContext";
import ReactMarkdown from "react-markdown";
import { ResponsiveLine } from "@nivo/line";

const CATEGORY_LABELS = {
    valuation_score: { label: 'Bewertung', weight: 20 },
    growth_score: { label: 'Wachstum', weight: 20 },
    profitability_score: { label: 'Profitabilität', weight: 15 },
    balance_sheet_score: { label: 'Bilanz', weight: 15 },
    earnings_score: { label: 'Earnings', weight: 10 },
    analyst_score: { label: 'Analysten/Insider', weight: 10 },
    news_score: { label: 'News', weight: 5 },
    technical_score: { label: 'Technicals', weight: 5 },
    dividend_score: { label: 'Dividende', weight: 0 },
};

const STRATEGY_LABELS = {
    growth: { label: 'Wachstum', color: '#2196f3' },
    dividend: { label: 'Dividende', color: '#ff9800' },
    longterm: { label: 'Langfristig', color: '#9c27b0' },
};

const SIGNAL_COLORS = { GREEN: '#4caf50', YELLOW: '#ff9800', RED: '#f44336' };

function SignalBadge({ signal, score }) {
    const color = SIGNAL_COLORS[signal] || '#9e9e9e';
    return (
        <Chip
            label={`${signal} — ${score != null ? Math.round(score) : '?'}`}
            sx={{
                backgroundColor: color, color: '#fff',
                fontWeight: 'bold', fontSize: '1.1rem',
                height: 36, px: 2,
            }}
        />
    );
}

function CategoryBar({ label, score, weight }) {
    const missing = score == null;
    const value = missing ? 0 : Math.max(0, Math.min(100, score));
    const barColor = value >= 80 ? '#4caf50' : value >= 55 ? '#ff9800' : '#f44336';

    return (
        <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.3 }}>
                <Typography variant="body2" sx={{ opacity: missing ? 0.4 : 1 }}>
                    {label} ({weight}%)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', opacity: missing ? 0.4 : 1 }}>
                    {missing ? 'Keine Daten' : Math.round(score)}
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                    height: 8, borderRadius: 4,
                    backgroundColor: 'rgba(128,128,128,0.2)',
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: missing ? '#9e9e9e' : barColor,
                        borderRadius: 4,
                    },
                }}
            />
        </Box>
    );
}

function ScoreHistoryChart({ history, themeMode }) {
    if (!history || history.length < 2) {
        return <Typography variant="body2" sx={{ opacity: 0.6 }}>Noch nicht genug Daten für den Verlauf.</Typography>;
    }

    const data = [{
        id: 'Score',
        data: history.map(h => ({
            x: h.as_of_date?.substring(0, 10),
            y: Number(h.portfolio_action_score),
        })),
    }];

    const textColor = themeMode === 'dark' ? '#e0e0e0' : '#333';

    return (
        <Box sx={{ height: 250 }}>
            <ResponsiveLine
                data={data}
                margin={{ top: 20, right: 20, bottom: 50, left: 40 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0, max: 100 }}
                axisBottom={{
                    tickRotation: -45,
                    legendOffset: 36,
                }}
                axisLeft={{
                    legendOffset: -36,
                }}
                colors={['#2196f3']}
                pointSize={8}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enableArea={false}
                enableGridX={false}
                theme={{
                    axis: { ticks: { text: { fill: textColor, fontSize: 10 } } },
                    grid: { line: { stroke: 'rgba(128,128,128,0.2)' } },
                }}
                markers={[
                    { axis: 'y', value: 80, lineStyle: { stroke: '#4caf50', strokeWidth: 1, strokeDasharray: '4 4' } },
                    { axis: 'y', value: 55, lineStyle: { stroke: '#ff9800', strokeWidth: 1, strokeDasharray: '4 4' } },
                ]}
            />
        </Box>
    );
}

function LLMSection({ snapshot, identifier, user, onUpdate }) {
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [models, setModels] = useState([]);
    const isAdmin = user?.role === 'admin';
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [promptOpen, setPromptOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const hasLLM = snapshot.llm_summary_full || snapshot.llm_summary_short;

    // Load available models once (admin only)
    useEffect(() => {
        if (!isAdmin) return;
        const base = process.env.REACT_APP_API_URI;
        fetch(`${base}/api/ai_model_weights`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) setModels(data);
            })
            .catch(() => {});
    }, [isAdmin]);

    const handleGenerate = async (overrideProvider, overrideModel) => {
        setGenerating(true);
        setError(null);
        setMenuAnchor(null);
        try {
            const base = process.env.REACT_APP_API_URI;
            const body = { puser: user.p_user, identifier };
            if (snapshot?.run_id == null) {
                body.snapshot = snapshot;
            }
            if (overrideProvider) body.llmProvider = overrideProvider;
            if (overrideModel) body.model = overrideModel;

            const response = await fetch(`${base}/api/trafficlight/generate-llm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await response.json();
            if (response.ok && data.llm_summary_full) {
                onUpdate(prev => ({
                    ...prev,
                    llm_summary_short: data.llm_summary_short,
                    llm_summary_full: data.llm_summary_full,
                    llm_generated_at: data.llm_generated_at,
                    llm_model: data.llm_model,
                }));
            } else {
                setError(data.message || 'Generierung fehlgeschlagen');
            }
        } catch (err) {
            setError(err.message);
        }
        setGenerating(false);
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>KI-Analyse</Typography>
                {isAdmin ? (
                    <ButtonGroup variant="outlined" size="small" disabled={generating}>
                        <Button
                            startIcon={generating ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                            onClick={() => handleGenerate()}
                        >
                            {hasLLM ? 'Neu generieren' : 'Analyse generieren'}
                        </Button>
                        <Button
                            size="small"
                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                            sx={{ px: 0.5, minWidth: 'auto' }}
                        >
                            <ArrowDropDownIcon />
                        </Button>
                    </ButtonGroup>
                ) : (
                    <Button
                        variant="outlined" size="small"
                        startIcon={generating ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                        onClick={() => handleGenerate()}
                        disabled={generating}
                    >
                        {hasLLM ? 'Neu generieren' : 'Analyse generieren'}
                    </Button>
                )}
                {isAdmin && (
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                    >
                        {models.map((m) => (
                            <MenuItem
                                key={m.model}
                                onClick={() => handleGenerate(m.llmprovider, m.model)}
                                sx={{ fontSize: '0.85rem' }}
                            >
                                <Stack>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{m.model}</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.6 }}>{m.llmprovider}</Typography>
                                </Stack>
                            </MenuItem>
                        ))}
                        {models.length === 0 && (
                            <MenuItem disabled><Typography variant="body2">Keine Modelle konfiguriert</Typography></MenuItem>
                        )}
                    </Menu>
                )}
            </Stack>
            {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}
            {hasLLM && (snapshot.llm_generated_at || snapshot.crea_date) && (
                <Typography variant="body2" sx={{ mb: 1, opacity: 0.6 }}>
                    KI-Analyse vom: {formatDateTime(snapshot.llm_generated_at || snapshot.crea_date)}
                    {snapshot.llm_model && ` · Modell: ${snapshot.llm_model}`}
                </Typography>
            )}
            {snapshot.llm_summary_full ? (
                <Box>
                    <ReactMarkdown>{snapshot.llm_summary_full}</ReactMarkdown>
                </Box>
            ) : snapshot.llm_summary_short ? (
                <ReactMarkdown>{snapshot.llm_summary_short}</ReactMarkdown>
            ) : (
                <Typography variant="body2" sx={{ opacity: 0.5 }}>
                    Noch keine KI-Analyse vorhanden. Klicke auf "Analyse generieren" um einen Report zu erstellen.
                </Typography>
            )}
            {snapshot.llm_prompt && (
                <Box sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            size="small" variant="text"
                            startIcon={<ExpandMoreIcon sx={{ transform: promptOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }} />}
                            onClick={() => setPromptOpen(prev => !prev)}
                            sx={{ textTransform: 'none', opacity: 0.7 }}
                        >
                            Prompt anzeigen
                        </Button>
                        {promptOpen && (
                            <Tooltip title={copied ? 'Kopiert!' : 'Prompt kopieren'} arrow>
                                <IconButton size="small" onClick={() => {
                                    navigator.clipboard.writeText(snapshot.llm_prompt);
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 2000);
                                }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                    <Collapse in={promptOpen}>
                        <Box sx={{
                            mt: 1, p: 2,
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            borderRadius: 1,
                            maxHeight: 500, overflow: 'auto',
                            fontFamily: 'monospace', fontSize: '0.8rem',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                            {snapshot.llm_prompt}
                        </Box>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
}

const TrafficLightDetail = ({ identifier, snapshotOverride = null }) => {
    const { user } = useContext(UserContext);
    const theme = useTheme();
    const [snapshot, setSnapshot] = useState(snapshotOverride || null);
    const [history, setHistory] = useState([]);
    const [prevSnapshot, setPrevSnapshot] = useState(null);

    useEffect(() => {
        setSnapshot(snapshotOverride || null);
    }, [snapshotOverride, identifier]);

    useEffect(() => {
        if (!user?.p_user || !identifier) return;
        const base = process.env.REACT_APP_API_URI;

        fetch(`${base}/api/trafficlight/detail?puser=${user.p_user}&identifier=${identifier}`)
            .then(r => r.json())
            .then(data => {
                if (data && !data.message) setSnapshot(data);
            })
            .catch(console.error);

        fetch(`${base}/api/trafficlight/history?puser=${user.p_user}&identifier=${identifier}&weeks=12`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) setHistory(data);
            })
            .catch(console.error);

        fetch(`${base}/api/trafficlight?puser=${user.p_user}`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) {
                    const prev = data.find(d => d.identifier === identifier);
                    if (prev) setPrevSnapshot(prev);
                }
            })
            .catch(console.error);
    }, [user, identifier]);

    if (!snapshot) {
        return (
            <Box sx={{ p: 2, opacity: 0.6 }}>
                <Typography variant="body2">Keine Ampel-Daten verfügbar. Klicke oben auf "Neu bewerten" um eine Analyse zu starten.</Typography>
            </Box>
        );
    }

    const reasons = snapshot.reasons_json;
    const overlayDetails = reasons?.overlay || [];

    return (
        <Box sx={{ p: 0, mt: 2 }}>
            {/* Signal + Score Header */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <SignalBadge signal={snapshot.signal} score={snapshot.portfolio_action_score} />
                {snapshot.strategy && STRATEGY_LABELS[snapshot.strategy] && (
                    <Chip
                        label={STRATEGY_LABELS[snapshot.strategy].label}
                        size="small"
                        sx={{
                            backgroundColor: STRATEGY_LABELS[snapshot.strategy].color,
                            color: '#fff', fontWeight: 'bold',
                        }}
                    />
                )}
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Coverage: {snapshot.coverage_categories_present}/{snapshot.coverage_categories_total} ({snapshot.coverage_pct}%)
                </Typography>
                {prevSnapshot?.signal_prev && prevSnapshot.signal_prev !== snapshot.signal && (
                    <Typography variant="body2">
                        Vorwoche: {prevSnapshot.signal_prev} {Math.round(prevSnapshot.score_prev)} →
                        {snapshot.signal} {Math.round(Number(snapshot.portfolio_action_score))}
                        {prevSnapshot.score_delta != null && (
                            <span style={{ color: prevSnapshot.score_delta > 0 ? '#4caf50' : '#f44336', fontWeight: 'bold', marginLeft: 4 }}>
                                ({prevSnapshot.score_delta > 0 ? '+' : ''}{Math.round(prevSnapshot.score_delta)})
                            </span>
                        )}
                    </Typography>
                )}
            </Stack>

            <Grid container spacing={3}>
                {/* Category Scores */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Teil-Scores</Typography>
                    {Object.entries(CATEGORY_LABELS).map(([key, { label, weight }]) => (
                        <CategoryBar key={key} label={label} score={snapshot[key]} weight={weight} />
                    ))}
                </Grid>

                {/* Overlay + Score History */}
                <Grid item xs={12} md={6}>
                    {/* Portfolio Overlay */}
                    {overlayDetails.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Portfolio-Overlay</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Asset Score: {Math.round(Number(snapshot.asset_score))} → Action Score: {Math.round(Number(snapshot.portfolio_action_score))}
                                <span style={{ color: '#f44336', marginLeft: 8 }}>({snapshot.portfolio_overlay} Punkte)</span>
                            </Typography>
                            {overlayDetails.map((d, i) => (
                                <Typography key={i} variant="body2" sx={{ ml: 1, opacity: 0.8 }}>
                                    • {d.rule}: {d.value} → {d.penalty} Punkte
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {/* Score History Chart */}
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Score-Verlauf (12 Wochen)</Typography>
                    <ScoreHistoryChart history={history} themeMode={theme.palette.mode} />
                </Grid>
            </Grid>

            {/* KI-Analyse — full width below scores */}
            <Box sx={{ mt: 3 }}>
                <LLMSection snapshot={snapshot} identifier={identifier} user={user} onUpdate={setSnapshot} />
            </Box>
        </Box>
    );
};

export default TrafficLightDetail;
